import React, { useState, useEffect, useRef } from "react";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import { Button } from "@material-ui/core";
import { Formik, Form } from "formik";
import * as Yup from "yup";

// Config
import colors from "../../config/colors";
import { getPhoneNumberFormat } from "../../utils/formatUtils";

// Languages
import { language, getTextSingle } from "../../languages/Languages";

// Hooks
import useOnErrorScroll from "../../hooks/useOnErrorScroll";

// Components
import FormInput from "../ContactForm/FormInput";
import SelectInput from "../ContactForm/SelectInput";
import Text, { types } from "../Text/Text";
import { getDemoVideoUrl } from "../../config/Api";
import LoadingCircle from "../LoadingCircle/LoadingCircle";

const useStyles = makeStyles(theme => ({
  root: {
    display: "block",
    position: "relative"
  },
  bigIcon: {
    marginTop: theme.spacing(6)
  },
  formSection: {
    padding: theme.spacing(4)
  },
  field: {
    marginBottom: `${theme.spacing(1)}px`
  },
  submit: {
    color: colors.white,
    minWidth: 180,
    width: "6vw",
    padding: theme.spacing(1, 5),
    borderRadius: 40,
    border: "none",
    fontFamily: ["Montserrat", "sans-serif"].join(","),
    fontWeight: 600,
    textTransform: "initial",
    [theme.breakpoints.down("sm")]: {
      borderRadius: 8,
      height: 70
    },
    [theme.breakpoints.down("xs")]: {
      width: "100%"
    },
    [theme.breakpoints.up("lg")]: {
      fontSize: "0.6vw"
    },
    "& .MuiButton-label": {
      display: "flex",
      justifyContent: "space-evenly"
    },
    "&.Mui-disabled": {
      color: colors.mineShaft,
      background: colors.grey
    }
  },
  submitSent: {
    backgroundColor: colors.pastelGreen
  },
  loadingWrapper: {
    position: "absolute",
    height: "100%",
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: "999",
    top: "0",
    left: "0"
  }
}));

const inputNames = {
  UserType: "UserType",
  OrganizationName: "OrganizationName",
  GivenName: "GivenName",
  FamilyName: "FamilyName",
  Email: "Email",
  PhoneNumber: "PhoneNumber"
};

const inputNamesOrdered = [
  inputNames.OrganizationName,
  inputNames.PhoneNumber,
  inputNames.Email,
  inputNames.PhoneNumber
];

const formSchema = Yup.object().shape({
  UserType: Yup.string().required(
    getTextSingle(language.formGeneric.errors.required)
  ),
  OrganizationName: Yup.string()
    .max(50, getTextSingle(language.formGeneric.errors.tooLong))
    .required(getTextSingle(language.formGeneric.errors.required)),
  GivenName: Yup.string().required(
    getTextSingle(language.formGeneric.errors.required)
  ),
  FamilyName: Yup.string().required(
    getTextSingle(language.formGeneric.errors.required)
  ),
  PhoneNumber: Yup.string()
    .max(50, getTextSingle(language.formGeneric.errors.tooLong))
    .matches(
      getPhoneNumberFormat(),
      getTextSingle(language.formGeneric.errors.invalidPhone)
    )
    .required(getTextSingle(language.formGeneric.errors.required)),
  Email: Yup.string()
    .email(getTextSingle(language.formGeneric.errors.invalidEmail))
    .required(getTextSingle(language.formGeneric.errors.required))
});

const noUserTypeSchema = Yup.object().shape({
  OrganizationName: Yup.string()
    .max(50, getTextSingle(language.formGeneric.errors.tooLong))
    .required(getTextSingle(language.formGeneric.errors.required)),
  GivenName: Yup.string().required(
    getTextSingle(language.formGeneric.errors.required)
  ),
  FamilyName: Yup.string().required(
    getTextSingle(language.formGeneric.errors.required)
  ),
  PhoneNumber: Yup.string()
    .max(50, getTextSingle(language.formGeneric.errors.tooLong))
    .matches(
      getPhoneNumberFormat(),
      getTextSingle(language.formGeneric.errors.invalidPhone)
    )
    .required(getTextSingle(language.formGeneric.errors.required)),
  Email: Yup.string()
    .email(getTextSingle(language.formGeneric.errors.invalidEmail))
    .required(getTextSingle(language.formGeneric.errors.required))
});

export const sources = {
  LEARNING: "learningMaterials", // default
  BLUEPRINT: "blueprint",
  BOOK_DEMO: "bookDemo"
};

const DemoVideoForm = ({
  className,
  hideUserType,
  source,
  isSent,
  onSuccess = () => {},
  buttonText
}) => {
  const classes = useStyles();
  const [isError, setError] = useState(false);
  const [checkingSubmiting, setCheckingSubmiting] = useState(false);
  // Text
  const { form } = language.pages.demoVideos;
  const isMounted = useRef(true);

  const handleSubmit = (values, { setSubmitting }) => {
    setSubmitting(true);

    fetch(getDemoVideoUrl(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ ...values, Source: source })
    })
      .then(res => {
        if (!res.ok) {
          throw new Error(`Server error: ${res.status}`);
        }
        return res.json();
      })
      .then(data => {
        if (isMounted.current) {
          onSuccess(data);
          setSubmitting(false);
        }
      })
      .catch(err => {
        setError(err);
        setSubmitting(false);
      });
  };

  useEffect(
    () => () => {
      isMounted.current = false;
    },
    []
  );

  return (
    <Formik
      initialValues={{
        OrganizationName: "",
        PhoneNumber: "",
        Email: "",
        GivenName: "",
        FamilyName: "",
        UserType: ""
      }}
      validationSchema={hideUserType ? noUserTypeSchema : formSchema}
      onSubmit={handleSubmit}
    >
      {({ errors, touched, handleChange, isSubmitting, setTouched }) => {
        useOnErrorScroll(
          errors,
          inputNamesOrdered,
          checkingSubmiting,
          newTouched => {
            setTouched({ ...touched, ...newTouched });
            setCheckingSubmiting(false);
          }
        );
        return (
          <Form className={`${classes.root} ${className}`}>
            {!hideUserType && (
              <SelectInput
                disabled={isSubmitting}
                name="UserType"
                label={getTextSingle(form.userType)}
                placeholder="Please select"
                options={[
                  { value: "SupportService", label: "Support Service" },
                  { value: "PoliceOfficer", label: "Police Officer" }
                ]}
              />
            )}

            <FormInput
              disabled={isSubmitting}
              className={classes.field}
              name="OrganizationName"
              variant="outlined"
              label={getTextSingle(form.organization)}
              error={errors.OrganizationName}
              touched={touched.OrganizationName}
              onChange={handleChange}
            />
            <FormInput
              disabled={isSubmitting}
              className={classes.field}
              name="GivenName"
              variant="outlined"
              label={getTextSingle(form.givenName)}
              error={errors.GivenName}
              touched={touched.GivenName}
              onChange={handleChange}
            />
            <FormInput
              disabled={isSubmitting}
              className={classes.field}
              name="FamilyName"
              variant="outlined"
              label={getTextSingle(form.familyName)}
              error={errors.FamilyName}
              touched={touched.FamilyName}
              onChange={handleChange}
            />
            <FormInput
              disabled={isSubmitting}
              className={classes.field}
              name="Email"
              type="email"
              variant="outlined"
              label={getTextSingle(form.email)}
              error={errors.Email}
              touched={touched.Email}
              onChange={handleChange}
            />
            <FormInput
              disabled={isSubmitting}
              className={classes.field}
              name="PhoneNumber"
              type="tel"
              variant="outlined"
              label={getTextSingle(form.phoneNumber)}
              error={errors.PhoneNumber}
              touched={touched.PhoneNumber}
              onChange={handleChange}
            />
            <Button
              disabled={isSubmitting}
              className={clsx(classes.submit, {
                [classes.submitSent]: isSent
              })}
              type="submit"
              color="secondary"
              variant="contained"
              onClick={() => {
                setCheckingSubmiting(true);
              }}
            >
              {buttonText || getTextSingle(form.send)}
            </Button>
            {isError && (
              <Text type={types.fieldError}>
                {getTextSingle(language.formGeneric.errors.error)}
              </Text>
            )}
            {isSubmitting && (
              <div className={classes.loadingWrapper}>
                <LoadingCircle />
              </div>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

export default DemoVideoForm;
